import { inject, Injectable } from "@angular/core";
import { ArticlesInfosFacadeService } from "../../articles/services/article-infos/article-infos-facade.service";
import { ArticleInfosHelperService } from "../../articles/services/article-infos/article-infos-helper.service";

/**
 * Enriches BL PDF payloads with frontend-computed data.
 * Poids = uniquement brut : gross_weight_uc × quantiteArticle (UG).
 * Pas de notion de poids net dans ce flux.
 */
@Injectable({ providedIn: "root" })
export class BlPayloadEnricherService {
  private articleInfosFacade = inject(ArticlesInfosFacadeService);

  enrichPayload(payload: any): any {
    if (!payload) return payload;

    if (Array.isArray(payload)) {
      return payload.map((p) => this.enrichSingleBl(p));
    }

    if (payload.bls && Array.isArray(payload.bls)) {
      return {
        ...payload,
        bls: payload.bls.map((bl: any) => this.enrichSingleBl(bl)),
      };
    }

    return this.enrichSingleBl(payload);
  }

  private enrichSingleBl(bl: any): any {
    if (!bl) return bl;

    let totalPoidsBrut = 0;

    // Enrich expeditions (line items)
    const expeditions = (bl.expeditions ?? []).map((exp: any) => {
      const ai = this.getArticleInfos(exp.articleId ?? exp.articleInfos?.id);
      const grossWeightUc = ai?.gross_weight_uc ?? 0;
      const grossWeightColis = ai?.gross_weight_colis ?? 0;
      const qtyArticle = Number(exp.quantiteArticle || exp.quantite || 0);
      const poidsBrutLigne = grossWeightUc > 0
        ? +(qtyArticle * grossWeightUc).toFixed(1)
        : null;

      if (poidsBrutLigne) totalPoidsBrut += poidsBrutLigne;

      return {
        ...exp,
        grossWeightUc,
        grossWeightColis,
        poidsBrutLigne,
        uniteDenomination: ArticleInfosHelperService.uniteDenomination(ai),
      };
    });

    // Enrich palettes — si le backend a calculé les poids, les garder ;
    // sinon recalculer depuis les lots (gross_weight_uc × quantiteArticle)
    const palettes = (bl.palettes ?? []).map((palette: any) => {
      if (palette.poidsBrutPalette > 0) {
        totalPoidsBrut += palette.poidsBrutPalette;
        return palette;
      }

      // Fallback : recalculer depuis les lots
      let poidsProduits = 0;
      const lots = (palette.lots ?? []).map((l: any) => {
        const gwUc = Number(l.grossWeightUc || 0);
        const qtyArticle = Number(l.quantiteArticle || l.quantite || 0);
        const poidsBrutLot = gwUc > 0 ? +(qtyArticle * gwUc).toFixed(1) : 0;
        poidsProduits += poidsBrutLot;
        return l;
      });

      const tare = palette.tarePalette ?? palette.customTare ?? palette.expeditionUnitType?.tare ?? 0;
      const poidsBrut = poidsProduits + tare;
      totalPoidsBrut += poidsBrut;

      return {
        ...palette,
        lots,
        poidsProduits: +poidsProduits.toFixed(1),
        poidsBrutPalette: +poidsBrut.toFixed(1),
        tarePalette: +tare.toFixed(1),
      };
    });

    return {
      ...bl,
      expeditions,
      palettes,
      totalPoidsBrut: +totalPoidsBrut.toFixed(1),
    };
  }

  private getArticleInfos(articleId: string) {
    if (!articleId) return null;
    return this.articleInfosFacade.getByArticleId(articleId)?.() ?? null;
  }
}
